import React, { useMemo, useRef, useState } from 'react';
import { RequireOrg, VpState } from '../components/VelopulseShell';
import useLoad from '../utils/useLoad';
import { vpApi, errorMessage, fmtMoney, fmtWan } from '../api/velopulse';

const EMPTY_FILTERS = {
  city: '',
  district: '',
  type: '',
  price_min: '',
  price_max: '',
  ping_min: '',
  ping_max: '',
  age_max: '',
};

function toWanParam(v) {
  if (v === '' || v == null) return undefined;
  const n = Number(v);
  if (!Number.isFinite(n)) return undefined;
  return Math.round(n * 10000);
}

function ListingCard({ item, selected, onSelect }) {
  return (
    <button type="button" className={`vp-listing${selected ? ' on' : ''}`} onClick={() => onSelect(item)}>
      <div className="vp-listing-top">
        <strong>{item.title}</strong>
        <span className="vp-badge indigo">{item.property_type_label}</span>
      </div>
      <div className="vp-price">{fmtWan(item.price)}</div>
      <div className="vp-sub">{item.city}{item.district} · {item.ping != null ? `${item.ping} 坪` : '坪數未提供'} · {item.age != null ? `屋齡 ${item.age} 年` : '屋齡未提供'}</div>
      {item.rooms && <div className="vp-sub">{item.rooms}{item.floor ? ` · ${item.floor}` : ''}</div>}
    </button>
  );
}

function ListingDetail({ item, onPhotoChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [photoErr, setPhotoErr] = useState('');
  const [detail, setDetail] = useState(item);
  const [loadingDetail, setLoadingDetail] = useState(false);

  React.useEffect(() => {
    setDetail(item);
    setPhotoErr('');
    if (!item?.id || item.is_mock) return undefined;
    let cancelled = false;
    setLoadingDetail(true);
    vpApi.getRealtyListing(item.id)
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch(() => { /* 詳情載入失敗仍顯示列表資料 */ })
      .finally(() => { if (!cancelled) setLoadingDetail(false); });
    return () => { cancelled = true; };
  }, [item?.id, item?.is_mock]);

  if (!item) return <VpState text="點選左側物件查看詳情。" />;

  const rows = [
    ['縣市', `${detail.city}${detail.district}`],
    ['地址', detail.address || '—'],
    ['類型', detail.property_type_label],
    ['總價', fmtMoney(detail.price)],
    ['坪數', detail.ping != null ? `${detail.ping} 坪` : '—'],
    ['屋齡', detail.age != null ? `${detail.age} 年` : '—'],
    ['格局', detail.rooms || '—'],
    ['樓層', detail.floor || '—'],
  ];

  const photos = detail.photos || [];
  const canEditPhotos = detail && !detail.is_mock;

  const uploadPhoto = async (file) => {
    if (!file || !canEditPhotos) return;
    setUploading(true);
    setPhotoErr('');
    try {
      const photo = await vpApi.addRealtyListingPhoto(detail.id, { file });
      setDetail((d) => ({ ...d, photos: [...(d.photos || []), photo] }));
      onPhotoChange?.();
    } catch (err) {
      setPhotoErr(errorMessage(err, '照片上傳失敗。'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removePhoto = async (photoId) => {
    if (!canEditPhotos) return;
    if (!window.confirm('確定刪除這張照片？')) return;
    setPhotoErr('');
    try {
      await vpApi.deleteRealtyListingPhoto(detail.id, photoId);
      setDetail((d) => ({ ...d, photos: (d.photos || []).filter((p) => p.id !== photoId) }));
      onPhotoChange?.();
    } catch (err) {
      setPhotoErr(errorMessage(err, '照片刪除失敗。'));
    }
  };

  return (
    <div>
      <div className="vp-detail-head">
        <h1>{detail.title}</h1>
        <span className="vp-badge indigo">{detail.property_type_label}</span>
      </div>
      <div className="vp-price" style={{ fontSize: 22, marginBottom: 12 }}>{fmtWan(detail.price)}</div>
      <div className="vp-fields">
        {rows.map(([k, v]) => (
          <div className="vp-kv" key={k}><div className="k">{k}</div><div className="v">{v}</div></div>
        ))}
      </div>
      {detail.description && <p className="vp-hint">{detail.description}</p>}
      {detail.is_mock && <p className="vp-disclaimer">示範物件，非正式掛牌。</p>}

      <div style={{ marginTop: 16 }}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>物件照片</h3>
        {loadingDetail && <p className="vp-muted">載入照片中…</p>}
        {photos.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
            {photos.map((p) => (
              <div key={p.id} style={{ position: 'relative', width: 96, height: 72, borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                <img src={p.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                {canEditPhotos && (
                  <button
                    type="button"
                    className="vp-btn danger"
                    style={{ position: 'absolute', top: 2, right: 2, padding: '2px 6px', fontSize: 11 }}
                    onClick={() => removePhoto(p.id)}
                  >
                    刪
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        {!photos.length && !loadingDetail && <p className="vp-muted">尚無照片。</p>}
        {canEditPhotos && (
          <div className="vp-bar" style={{ marginTop: 8 }}>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              style={{ display: 'none' }}
              onChange={(e) => uploadPhoto(e.target.files?.[0])}
            />
            <button type="button" className="vp-btn" disabled={uploading} onClick={() => fileRef.current?.click()}>
              {uploading ? '上傳中…' : '上傳照片'}
            </button>
          </div>
        )}
        {photoErr && <p className="vp-disclaimer" style={{ color: '#b91c1c' }}>{photoErr}</p>}
      </div>
    </div>
  );
}

function ImportPanel({ onImported }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState('');

  const downloadTemplate = async () => {
    setErr('');
    try {
      const res = await vpApi.downloadRealtyImportTemplate();
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'realty-listings-template.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr(errorMessage(e, '範本下載失敗。'));
    }
  };

  const onFile = async (file) => {
    if (!file) return;
    setBusy(true);
    setErr('');
    setResult(null);
    try {
      const data = await vpApi.importRealtyListings(file);
      setResult(data);
      onImported?.();
    } catch (e) {
      setErr(errorMessage(e, '匯入失敗。'));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="vp-card" style={{ marginTop: 12 }}>
      <h2 style={{ margin: '0 0 8px' }}>自有物件匯入</h2>
      <p className="vp-hint" style={{ marginTop: 0 }}>
        這是貴公司自有物件匯入，不是外站爬蟲。請用公司內部 CSV／Excel（另存 CSV 亦可），勿爬取 591、樂屋等外部網站。
      </p>
      <div className="vp-bar" style={{ marginBottom: 0 }}>
        <button type="button" className="vp-btn" onClick={downloadTemplate}>下載 CSV 範本</button>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          style={{ display: 'none' }}
          onChange={(e) => onFile(e.target.files?.[0])}
        />
        <button type="button" className="vp-btn primary" disabled={busy} onClick={() => fileRef.current?.click()}>
          {busy ? '匯入中…' : '上傳 CSV／Excel'}
        </button>
      </div>
      {err && <p className="vp-disclaimer" style={{ color: '#b91c1c', marginTop: 8 }}>{err}</p>}
      {result && (
        <div style={{ marginTop: 10, fontSize: 13 }}>
          <p style={{ margin: '0 0 4px' }}>
            成功 <strong>{result.imported}</strong> 筆
            {result.failed ? <>，失敗 <strong>{result.failed}</strong> 筆</> : null}
          </p>
          {!!result.errors?.length && (
            <ul style={{ margin: '6px 0 0', paddingLeft: 18, color: '#92400e' }}>
              {result.errors.slice(0, 10).map((e, i) => (
                <li key={`${e.row}-${i}`}>{e.row != null ? `第 ${e.row} 列：` : ''}{e.message}</li>
              ))}
              {result.errors.length > 10 && <li>…其餘錯誤已省略</li>}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function RealtySearch() {
  const [draft, setDraft] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [selected, setSelected] = useState(null);
  const set = (k) => (e) => {
    const v = e.target.value;
    setDraft((f) => {
      const next = { ...f, [k]: v };
      if (k === 'city') next.district = '';
      return next;
    });
  };

  const query = useMemo(() => ({
    city: applied.city || undefined,
    district: applied.district || undefined,
    type: applied.type || undefined,
    price_min: toWanParam(applied.price_min),
    price_max: toWanParam(applied.price_max),
    ping_min: applied.ping_min === '' ? undefined : Number(applied.ping_min),
    ping_max: applied.ping_max === '' ? undefined : Number(applied.ping_max),
    age_max: applied.age_max === '' ? undefined : Number(applied.age_max),
  }), [applied]);

  const { data, error, loading, reload } = useLoad(() => vpApi.listRealtyListings(query), [query]);

  const cityMeta = data?.meta?.cities || [];
  const types = data?.meta?.types || [];
  const districts = (cityMeta.find((c) => c.city === draft.city) || {}).districts || [];
  const items = data?.items || [];

  const apply = (e) => {
    e.preventDefault();
    setSelected(null);
    setApplied({ ...draft });
  };

  const reset = () => {
    setDraft(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setSelected(null);
  };

  const onImported = () => {
    setSelected(null);
    reload();
  };

  const selectListing = (item) => setSelected(item);

  return (
    <div className="vp-content wide vp-search">
      <div className="vp-hello">
        <div>
          <h1>搜房</h1>
          <p>以縣市、總價、坪數、屋齡與類型篩選物件。可匯入貴公司自有物件；組織尚無物件時顯示示範種子。</p>
        </div>
      </div>

      <div className="vp-warnbox" role="note">{data?.source_note || '此為示範物件資料，並非地政司或實價登錄即時查詢。'}</div>

      <ImportPanel onImported={onImported} />

      <form className="vp-card vp-search-filters" onSubmit={apply} style={{ marginTop: 12 }}>
        <div className="vp-bar" style={{ marginBottom: 0 }}>
          <select value={draft.city} onChange={set('city')} aria-label="縣市">
            <option value="">縣市：全部</option>
            {cityMeta.map((c) => <option key={c.city} value={c.city}>{c.city}</option>)}
          </select>
          <select value={draft.district} onChange={set('district')} aria-label="行政區" disabled={!draft.city}>
            <option value="">行政區：全部</option>
            {districts.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={draft.type} onChange={set('type')} aria-label="類型">
            <option value="">類型：全部</option>
            {types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <input type="number" min="0" placeholder="最低總價（萬）" value={draft.price_min} onChange={set('price_min')} aria-label="最低總價（萬）" />
          <input type="number" min="0" placeholder="最高總價（萬）" value={draft.price_max} onChange={set('price_max')} aria-label="最高總價（萬）" />
          <input type="number" min="0" step="0.1" placeholder="最小坪數" value={draft.ping_min} onChange={set('ping_min')} aria-label="最小坪數" />
          <input type="number" min="0" step="0.1" placeholder="最大坪數" value={draft.ping_max} onChange={set('ping_max')} aria-label="最大坪數" />
          <input type="number" min="0" placeholder="屋齡上限" value={draft.age_max} onChange={set('age_max')} aria-label="屋齡上限（年）" />
          <button type="submit" className="vp-btn primary">套用篩選</button>
          <button type="button" className="vp-btn" onClick={reset}>清除</button>
        </div>
      </form>

      {loading && <VpState loading />}
      {error && (
        <div className="vp-card" style={{ marginTop: 12 }}>
          <VpState text={errorMessage(error, '物件清單載入失敗。')} action={{ label: '重新載入', onClick: reload }} />
        </div>
      )}
      {!loading && !error && (
        <div className="vp-search-layout">
          <div>
            <p className="vp-muted" style={{ margin: '12px 0' }}>
              符合 {data?.matched ?? 0} 筆{data?.total != null ? `（共 ${data.total} 筆）` : ''}
              {data?.source === 'org' ? ' · 自有物件' : data?.source === 'mock' ? ' · 示範資料' : ''}
            </p>
            {!items.length && <div className="vp-card"><VpState text="沒有符合條件的物件，請放寬篩選或匯入自有物件。" /></div>}
            <div className="vp-listing-list">
              {items.map((item) => (
                <ListingCard key={item.id} item={item} selected={selected?.id === item.id} onSelect={selectListing} />
              ))}
            </div>
          </div>
          <div className="vp-card vp-listing-detail">
            <ListingDetail item={selected} onPhotoChange={reload} />
          </div>
        </div>
      )}
    </div>
  );
}

export default function RealtySearchPage() {
  return <RequireOrg><RealtySearch /></RequireOrg>;
}
